<template>
  <div class="swiper">
    <div
      ref="wrapper"
      class="swiper-wrapper"
      :class="{ animation }"
    >
      <div
        ref="slides"
        v-touch:start="handlerStart"
        v-touch:end="handlerEnd"
        class="swiper-slides"
        @mouseover="handlerStart"
        @mouseleave="handlerEnd"
      >
        <div
          v-for="(item, i) in items"
          :key="i"
          :data-slide-index="i"
          class="swiper-slide"
          :style="itemStyle"
        >
          <transition name="fade-slide-item">
            <template v-if="check(i)">
              <slot :item="item" :is-active="i === currentSlide" :index="i" />
            </template>
          </transition>
        </div>
      </div>
    </div>
    <template v-if="items.length > slidesToShowComputed">
      <client-only>
        <div>
          <div v-if="arrows && adaptive.isDesktop" class="arrows">
            <div
              v-for="direction in ['left', 'right']"
              :key="direction"
              :class="[`arrow-${direction}`]"
              @click="onSwipe(direction)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="#999999" d="M23 10.45L3.41357 10.45L10.1442 3.70742C10.5347 3.31691 10.5347 2.68339 10.1442 2.29288C9.7542 1.90237 9.12019 1.90237 8.73018 2.29288L0.293004 10.7301C0.246503 10.7766 0.204499 10.8281 0.167498 10.8836C0.151498 10.9076 0.140497 10.9341 0.126497 10.9596C0.108996 10.9911 0.0900047 11.0221 0.0760044 11.0556C0.0625041 11.0881 0.0544945 11.1226 0.0444943 11.1566C0.0359941 11.1851 0.025 11.2121 0.0194999 11.2421C-0.00650066 11.3711 -0.00650066 11.5046 0.0194999 11.6336C0.025 11.6636 0.0359941 11.6906 0.0444943 11.7196C0.0544945 11.7531 0.0625041 11.7876 0.0760043 11.8201C0.0900046 11.8541 0.108996 11.8846 0.126497 11.9161C0.140497 11.9416 0.151498 11.9681 0.167498 11.9921C0.204499 12.0476 0.246503 12.0991 0.293004 12.1456L8.73018 20.5828C8.92518 20.7778 9.18119 20.8758 9.43719 20.8758C9.6932 20.8758 9.9492 20.7778 10.1442 20.5828C10.5347 20.1918 10.5347 19.5592 10.1442 19.1682L3.41357 12.45L23 12.45C23.5525 12.45 24 11.9901 24 11.4381C24 10.8856 23.5525 10.45 23 10.45Z" />
              </svg>
            </div>
          </div>

          <div v-if="dots" :class="['dots', { 'dots-disabled': isIOS }]">
            <div v-for="(dot, i) in dynamicDots" :key="i" :style="dot.style" @click="onClickDot(i)" />
          </div>
        </div>
      </client-only>
    </template>
  </div>
</template>

<script>
import { debounce, throttle } from 'throttle-debounce';

import IOSDetect from '@/utils/device/ios-detect';

/**
 * Возвращает число между min и max
 * на основании value 0 ~ 1
 */
const interpolate = (v, [min, max]) => {
  let value = v;

  value = Math.abs(value);
  if (value > 1) value = 1;
  return min + (max - min) * value;
};

const easeOutQuad = (t) => t * (2 - t);

export default {
  name: 'ui-swiper',

  inject: ['adaptive'],

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    dots: {
      type: Boolean,
      default: () => false,
    },
    arrows: {
      type: Boolean,
      default: () => false,
    },
    slidesToShow: {
      type: Number,
      default: 1,
    },
    slidesToScroll: {
      type: Number,
      default: null,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    isMoving: false,
    isNavigationChange: false,
    scrollPosition: 0, // 0 ~ 1
    animation: false,
    timeout: null,
    isIOS: false,
    currentSlide: 0,
  }),

  computed: {
    isSlidingNow() {
      return this.animation || this.isMoving;
    },
    itemStyle() {
      return {
        flex: `0 0 ${100 / this.slidesToShow}%`,
      };
    },
    slidesToShowComputed() {
      return this.slidesToShow || this.slidesToScroll;
    },
    totalDots() {
      return Math.ceil(this.items.length / this.slidesToShowComputed);
    },
    currentDot() {
      // возвращает float, отсчет с 0
      return Math.round((this.totalDots - 1) * this.scrollPosition * 100) / 100;
    },
    dynamicDots() {
      return new Array(this.totalDots).fill().map((e, i) => ({
        style: {
          transform: `scale(${interpolate(i - this.currentDot, [1.35, 1])})`,
          opacity: interpolate(i - this.currentDot, [1, 0.4]),
        },
      }));
    },
    prevSlideIndex() {
      return Math.round(this.currentDot) - 1;
    },
    nextSlideIndex() {
      return Math.round(this.currentDot) + 1;
    },
    rangeIndexesToRender() {
      const rangeEndIndex = (this.currentSlide + 1) * this.slidesToShowComputed;
      const rangeStartIndex = Math.max(rangeEndIndex - this.slidesToShowComputed, 0);
      const minIndex = Math.max(rangeStartIndex - this.slidesToShowComputed, 0);
      const maxIndex = Math.min((rangeEndIndex + this.slidesToShowComputed - 1), this.items.length - 1);

      return { minIndex, maxIndex };
    },
  },

  mounted() {
    this.isIOS = IOSDetect();
    this.initScrollListener();
    this.initAutoplay();
  },

  beforeUnmount() {
    this?.$refs?.slides?.removeEventListener?.('scroll', this.setScrollPosition);
  },

  methods: {
    onSwipe(direction) {
      if (direction === 'left') {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
      this.$emit('swipe');
    },
    check(index) {
      const { minIndex, maxIndex } = this.rangeIndexesToRender;

      return index >= minIndex && index <= maxIndex;
    },
    handlerStart() {
      this.isMoving = true;
      this.isNavigationChange = false;
      this.stopAutoPlay();
    },
    handlerEnd() {
      this.isMoving = false;
      this.initAutoplay();
    },
    initScrollListener() {
      this.$refs.slides.addEventListener('scroll', this.setScrollPosition);
    },
    setScrollPosition: debounce(150, function () {
      const { slides } = this.$refs;
      this.scrollPosition = slides.scrollLeft / (slides.scrollWidth - slides.offsetWidth);
      this.onChangeScrollPosition();
    }),
    prevSlide() {
      this.isNavigationChange = true;
      if (this.currentDot === 0) {
        this.goToSlide(this.totalDots);
        return;
      }
      this.goToSlide(this.prevSlideIndex);
    },
    nextSlide() {
      this.isNavigationChange = true;
      if (Math.round(this.currentDot + 1) === this.totalDots) {
        this.goToSlide(0);
        return;
      }
      this.goToSlide(this.nextSlideIndex);
    },
    goToSlide(dotNumber) {
      this.stopAutoPlay();

      if (!this.$refs.slides) return;
      const targetPosition = this.$refs.slides.offsetWidth * dotNumber;
      this.animation = true;
      requestAnimationFrame((now) => this.smooth({ start: now, now, targetPosition }));
    },
    onClickDot(dotNumber) {
      this.isNavigationChange = false;
      this.goToSlide(dotNumber);
    },
    smooth({
      start, now, targetPosition, duration = 500,
    }) {
      if (!this.$refs.slides) return;

      const position = this.$refs.slides.scrollLeft;
      const elapsed = now - start;
      const progress = Math.ceil(easeOutQuad(elapsed / 500) * 100) / 100;
      const scrollPos = position + (targetPosition - position) * progress;
      this.$refs.slides.scrollLeft = scrollPos;

      if ((progress < 1 && progress >= 0)) {
        requestAnimationFrame((_now) => this.smooth({
          start, now: _now, targetPosition, duration,
        }));
      } else {
        this.animation = false;
        this.onChangeScrollPosition();
        this.initAutoplay();
      }
    },
    initAutoplay() {
      if (this.isIOS) return;
      if (this.autoplay) {
        this.timeout = setTimeout(() => {
          // если на тачах происходит скролл или курсор находится над баннером не переключаем слайд
          if (this.isSlidingNow) return;
          this.nextSlide();
        }, 4000);
      }
    },
    stopAutoPlay() {
      if (this.isIOS) return;
      clearTimeout(this.timeout);
    },
    onChangeScrollPosition: throttle(150, function () {
      if (this.animation) return;
      const currentSlide = Math.round(this.currentDot);
      if (this.currentSlide === currentSlide) return;
      this.currentSlide = currentSlide;
      this.$emit('change', this.currentSlide);
      if (this.isNavigationChange) {
        this.$emit('click-arrow');
      } else {
        this.$emit('swipe');
      }
    }),
  },
};
</script>

<style lang="scss">
.swiper {
  position: relative;
  &-wrapper {
    margin: 0 (-$containerGapMobile);
    overflow: hidden;
    position: relative;
  }
  &-slides {
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    flex-wrap: nowrap;
    padding: 0 calc($containerGapMobile / 2);
    padding-bottom: 50px;
    margin-bottom: -50px;
    scroll-padding: 0 calc($containerGapMobile / 2);
    scroll-snap-type: x mandatory;

    // TODO: Исправить на использование БЭМ-селекторов
    // stylelint-disable-next-line selector-max-universal
    > * {
      scroll-snap-align: start;
      padding: 0 calc($containerGapMobile / 2);
    }
    .animation & {
      scroll-snap-type: none;

      // TODO: Исправить на использование БЭМ-селекторов
      // stylelint-disable-next-line selector-max-universal
      > * {
        scroll-snap-align: initial;
      }
    }
    &::after {
      content: '';
      flex: 0 0 calc($containerGapMobile / 2);
    }
  }
  &-slide {
    > img {
      // кейс, когда item картинка
      width: 100%;
    }
  }
  .dots {
    display: flex;
    justify-content: center;
    align-items: center;
    bottom: -28px;

    // TODO: Исправить на использование БЭМ-селекторов
    // stylelint-disable-next-line selector-max-universal
    > * {
      width: 6px;
      height: 6px;
      background: $gray;
      margin: 16px;
      border-radius: 50%;
    }
  }
  .dots-disabled {
    pointer-events: none;
  }
  img {
    max-width: 100%;
  }
}
@include breakpoint("desktop") {
  .swiper {
    &-wrapper {
      display: grid;
      margin: 4px 0;
    }
    &-slides {
      margin: 0 -#{calc($containerGapMobile / 2)};
      padding: 0;
      scroll-padding: 0;
      padding-bottom: 50px;
      margin-bottom: -50px;
      &::after {
        display: none;
      }
    }
    .arrows {
      position: absolute;
      top: calc(50% - 94px);
      left: -12px;
      right: -12px;

      // TODO: Исправить на использование БЭМ-селекторов
      // stylelint-disable-next-line selector-max-universal
      > * {
        position: absolute;
        width: 48px;
        height: 48px;
        padding: 12px;
        background: $white;
        box-shadow: 0 0 0 1px $gray_lu;
        border-radius: 50%;
        cursor: pointer;

        // TODO: Исправить на использование БЭМ-селекторов
        // stylelint-disable-next-line selector-max-universal
        &.disabled {
          pointer-events: none;

          // TODO: Исправить на использование БЭМ-селекторов
          // stylelint-disable-next-line selector-max-universal
          svg {
            opacity: .5;
          }
        }
      }
      .arrow-right {
        right: 0;
        transform: rotate(180deg);
      }
    }
  }
}

.fade-slide-item {
  &-enter-active {
    transition: opacity .1s ease;
  }
  &-leave-active {
    display: none;
  }

  &-enter,
  &-leave-active {
    opacity: 0;
  }
}
</style>
